import { apiBase } from './apiBase';
import { apiFetch, ApiError } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ANAF e-Factura (SPV) integration — read-only mirror of the invoices the firm
// receives and sends through SPV, plus the per-firm OAuth connection. The
// backend keeps the CIF and the tokens; the UI only ever passes a companyId.

export type EfacturaKind = 'PRIMITA' | 'TRIMISA' | 'ERORI' | 'MESAJ_CUMPARATOR' | 'ALTUL';
export type EfacturaDocType = 'factura' | 'storno' | 'erori';

export type ConnectionState =
  | 'not_configured'
  | 'error'
  | 'pending'
  | 'connected'
  | 'no_spv_rights'
  | 'needs_reauth';

export interface EfacturaConnectionStatus {
  state: ConnectionState;
  message: string;
  cif: string | null;
  env: 'prod' | 'test';
  tokenExpiresAt: string | null;
  authorizedBy: string | null;
  messagesLastDay: number | null;
}

export interface EfacturaStatus {
  appConfigured: boolean;
  companyId: string;
  connection: EfacturaConnectionStatus;
  cachedMessages: number;
  lastSyncAt: string | null;
}

export interface EfacturaCompanySummary {
  companyId: string;
  name: string;
  cif: string;
  env: 'prod' | 'test';
  state: 'authorized' | 'pending';
}

export interface EfacturaMessageRow {
  id: string;
  companyId: string;
  anafId: string;
  indexIncarcare: string | null;
  kind: EfacturaKind;
  tip: string;
  dataCreare: string;
  partnerCif: string | null;
  partnerName: string | null;
  detalii: string | null;
  docType: EfacturaDocType | null;
  invoiceNumber: string | null;
  issueDate: string | null;
  dueDate: string | null;
  currency: string | null;
  // Decimal values arrive as strings; never add them in the UI.
  netAmount: string | null;
  vatAmount: string | null;
  totalAmount: string | null;
  payableAmount: string | null;
  vatAmountRon: string | null;
  hasArchive: boolean;
  hasPdf: boolean;
  /** Hand-kept payment status: received = achitată, sent = încasată. Null = open. */
  settledAt: string | null;
  syncError: string | null;
  syncedAt: string;
}

/** The parsed UBL document, exactly as the backend parser produced it (amounts are strings). */
export interface ParsedParty {
  name: string;
  vatId: string;
  registrationNumber: string;
  address: string;
}
export interface ParsedLine {
  id: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  lineTotal: string;
  vatCategory: string;
  vatPercent: string;
}
export interface ParsedInvoice {
  tip: 'factura' | 'storno';
  number: string;
  typeCode: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  supplier: ParsedParty;
  customer: ParsedParty;
  totals: {
    lineTotal: string;
    withoutVat: string;
    vat: string;
    vatRon: string;
    withVat: string;
    prepaid: string;
    payable: string;
  };
  vatBreakdown: Array<{
    taxableAmount: string;
    taxAmount: string;
    category: string;
    percent: string;
    exemptionReason: string;
  }>;
  payment: { meansCode: string; paymentId: string; iban: string };
  paymentReference: string;
  orderReference: string;
  billingReference: string;
  notes: string[];
  lines: ParsedLine[];
}
export interface ParsedErrors {
  tip: 'erori';
  errors: string[];
}
export type ParsedDocument = ParsedInvoice | ParsedErrors;

export interface EfacturaMessageDetail extends EfacturaMessageRow {
  details: ParsedDocument | null;
}

export interface EfacturaTotal {
  kind: EfacturaKind;
  currency: string | null;
  count: number;
  totalAmount: string | null;
}

export interface EfacturaListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface EfacturaListResponse {
  data: EfacturaMessageRow[];
  meta: EfacturaListMeta;
  totals: EfacturaTotal[];
}

export interface EfacturaFilters {
  companyId?: string;
  kind?: EfacturaKind;
  from?: string;
  to?: string;
  search?: string;
  /** 'true' = only settled, 'false' = only open. */
  settled?: 'true' | 'false';
  page?: number;
  limit?: number;
}

export interface EfacturaSyncStats {
  companyId: string;
  cif: string;
  listed: number;
  created: number;
  updated: number;
  failed: number;
  archived: number;
  partial: boolean;
  stopReason?: string;
  lastSyncAt: string;
}

export interface EfacturaSyncResult {
  companies: EfacturaSyncStats[];
  skipped: Array<{ companyId: string; reason: string }>;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export async function getEfacturaStatus(companyId?: string): Promise<EfacturaStatus> {
  const res = await apiFetch<Envelope<EfacturaStatus>>(
    `/api/anaf/efactura/status${query({ companyId })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function listEfacturaCompanies(): Promise<EfacturaCompanySummary[]> {
  const res = await apiFetch<Envelope<{ companies: EfacturaCompanySummary[] }>>(
    '/api/anaf/efactura/companies',
    { headers: authHeaders() },
  );
  return res.data.companies;
}

export async function startEfacturaAuthorization(
  companyId: string,
): Promise<{ authorizeUrl: string; hint: string; redirectUri: string }> {
  const res = await apiFetch<Envelope<{ authorizeUrl: string; hint: string; redirectUri: string }>>(
    '/api/anaf/efactura/connect',
    {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId }),
    },
  );
  return res.data;
}

export async function disconnectEfactura(companyId: string): Promise<{ revoked: boolean }> {
  const res = await apiFetch<Envelope<{ disconnected: true; revoked: boolean }>>(
    '/api/anaf/efactura/disconnect',
    {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId }),
    },
  );
  return { revoked: res.data.revoked };
}

export async function syncEfactura(input: {
  companyId?: string;
  zile?: number;
}): Promise<EfacturaSyncResult> {
  const res = await apiFetch<Envelope<EfacturaSyncResult>>('/api/anaf/efactura/sync', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function listEfacturaMessages(
  filters: EfacturaFilters = {},
): Promise<EfacturaListResponse> {
  // The list, its pagination and the per-kind totals all live inside the
  // envelope's data, not beside it.
  const res = await apiFetch<Envelope<EfacturaListResponse>>(
    `/api/anaf/efactura/messages${query({ ...filters })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

/** Mark a received invoice paid / a sent one collected (or clear the mark). */
export async function setEfacturaSettled(
  id: string,
  settled: boolean,
): Promise<EfacturaMessageRow> {
  const res = await apiFetch<Envelope<{ message: EfacturaMessageRow }>>(
    `/api/anaf/efactura/messages/${encodeURIComponent(id)}/settled`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ settled }),
    },
  );
  return res.data.message;
}

export async function getEfacturaMessage(id: string): Promise<EfacturaMessageDetail> {
  const res = await apiFetch<Envelope<{ message: EfacturaMessageDetail }>>(
    `/api/anaf/efactura/messages/${encodeURIComponent(id)}`,
    { headers: authHeaders() },
  );
  return res.data.message;
}

/**
 * Binary documents (PDF / XML / ZIP) need the Bearer header, so they cannot be
 * plain links. Fetch as a blob and hand back an object URL the caller opens or
 * downloads, then revokes.
 */
export async function fetchEfacturaFile(
  id: string,
  kind: 'pdf' | 'xml' | 'archive',
): Promise<{ url: string; blob: Blob }> {
  const res = await fetch(
    `${apiBase()}/api/anaf/efactura/messages/${encodeURIComponent(id)}/${kind}`,
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Descărcarea a eșuat (HTTP ${res.status}).`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { error?: { message?: string; code?: string } };
      message = body.error?.message ?? message;
      code = body.error?.code;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, message, code);
  }
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), blob };
}
